import React, { useMemo, useState } from "react";

function safeStringify(value) {
  const seen = new WeakSet();
  try {
    return JSON.stringify(
      value,
      (k, v) => {
        if (typeof v === "object" && v !== null) {
          if (seen.has(v)) return "[Circular]";
          seen.add(v);
        }
        if (typeof v === "function") return `[Function ${v.name || "anonymous"}]`;
        if (v instanceof Error) {
          return {
            name: v.name,
            message: v.message,
            stack: v.stack,
            cause: v.cause,
          };
        }
        return v;
      },
      2
    );
  } catch (e) {
    try {
      return String(value);
    } catch (_) {
      return "[Unserializable]";
    }
  }
}

function normalizeError(err) {
  if (!err) return null;
  if (err instanceof Error) return err;
  if (typeof err === "string") return new Error(err);
  try {
    const msg =
      err?.message ||
      err?.errorMsg ||
      err?.error ||
      (typeof err === "object" ? safeStringify(err) : String(err));
    const e = new Error(String(msg));
    e.name = err?.name || e.name;
    e.stack = err?.stack || e.stack;
    // Attach original for debugging
    e.original = err;
    return e;
  } catch (_) {
    return new Error(String(err));
  }
}

function pickAxiosDetails(err) {
  try {
    const resp = err?.response;
    if (!resp) return null;
    return {
      status: resp.status,
      statusText: resp.statusText,
      url: resp.config?.url,
      method: resp.config?.method,
      requestHeaders: resp.config?.headers,
      responseHeaders: resp.headers,
      responseData: resp.data,
    };
  } catch (_) {
    return null;
  }
}

export default function DebugErrorScreen({
  title = "Something went wrong",
  error,
  details,
  onDismiss,
  allowDismiss = true,
}) {
  const [copied, setCopied] = useState(false);
  const err = useMemo(() => normalizeError(error), [error]);
  const axiosDetails = useMemo(() => pickAxiosDetails(error), [error]);

  const payload = useMemo(() => {
    const now = new Date().toISOString();
    const url = typeof window !== "undefined" ? window.location.href : "";
    return {
      title,
      time: now,
      url,
      error: err
        ? {
            name: err.name,
            message: err.message,
            stack: err.stack,
            cause: err.cause,
            original: err.original,
          }
        : null,
      axios: axiosDetails,
      details: details || null,
    };
  }, [title, err, axiosDetails, details]);

  const textToCopy = useMemo(() => safeStringify(payload), [payload]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (_) {
      // ignore
    }
  };

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 99999,
        background: "#0b0d12",
        color: "#e8e8e8",
        overflow: "auto",
        padding: 20,
        fontFamily:
          'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif',
      }}
    >
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>{title}</div>
            <div style={{ opacity: 0.85, fontSize: 13 }}>
              The URL has NOT changed. Copy the details below for debugging.
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              onClick={copy}
              style={{
                background: "#2b6cb0",
                border: "1px solid #2b6cb0",
                color: "white",
                padding: "8px 10px",
                borderRadius: 8,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              {copied ? "Copied" : "Copy debug details"}
            </button>
            {allowDismiss && typeof onDismiss === "function" && (
              <button
                onClick={onDismiss}
                style={{
                  background: "transparent",
                  border: "1px solid #3a3f4b",
                  color: "#e8e8e8",
                  padding: "8px 10px",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Dismiss
              </button>
            )}
          </div>
        </div>

        <div style={{ height: 14 }} />

        {err?.message && (
          <div
            style={{
              background: "#121620",
              border: "1px solid #232a3b",
              borderRadius: 10,
              padding: 12,
              marginBottom: 12,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {err.message}
          </div>
        )}

        <pre
          style={{
            background: "#121620",
            border: "1px solid #232a3b",
            borderRadius: 10,
            padding: 12,
            margin: 0,
            fontSize: 12,
            lineHeight: 1.35,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {textToCopy}
        </pre>
      </div>
    </div>
  );
}
